import { UpdateCommentRequest, UpdateStudyReportRequest } from '../../../UI/interfaces';
import {
  QueryKeyT,
  useFetch_old,
  useFetch,
  useEdit,
  useDelete,
  usePaginated,
  useGenericMutation,
  useSingleFetch,
} from '../react-query';
import * as url from '../../urlHelper';
import { apiClient } from '../../apiHelper';
import { AxiosResponse } from 'axios';
import { reportQueryKeys } from './report-query-keys';

// export const useGetRecentReports = () => useFetch_old(`${url.GET_ALL_REPORTS}`);
// export const useGetStateReport = (StudyUID: string) => useFetch(`${url.GET_STATE_REPORT}/${StudyUID}`)
// export const useGetStateReport = (StudyUID: string, queryKey?: QueryKeyT) =>
//     useFetch(`${url.GET_STATE_REPORT}/${StudyUID}`, undefined, undefined, queryKey);
export function useGetRecentReports(config = {}) {
  const fetchKPIs = async () => {
    const response = await apiClient.get(`${url.GET_ALL_REPORTS}`);
    return response.data;
  };

  return useFetch(fetchKPIs, ['reports'], {});
}
export const useUpdateStateReport = (
  onSuccess?: (response: AxiosResponse, data: Record<string, unknown>) => void,
  onError?: (responce: AxiosResponse, error: Error) => void,
  queryKey?: QueryKeyT,
  updater?: (oldData: object[], newData: object) => object[]
) => {
  const updateStateReport = async (data: Record<string, unknown>) => {
    const response = await apiClient.put(`${url.UPDATE_STATE_REPORT}`, data);
    return response;
  };

  return useEdit<Record<string, unknown>>(
    updateStateReport,
    ['study_report_state'],
    'Failed to update report',
    'Report updated successfully',
    onSuccess,
    onError
  );
};

export const useGetGlobalReportByStudyId = (
  StudyUID: string,
  queryKey?: QueryKeyT
) =>
  useFetch_old(
    `${url.GET_GLOBAL_REPORT_VIEWS}/${StudyUID}`,
    undefined,
    {
      enabled: !!StudyUID,
      refetch: true,
      refetchOnWindowFocus: false,
    },
    queryKey
  );

export function useGetStudyReportViewsImages(
  StudyUID: string,
  enabled: boolean = true,
  queryKey?: QueryKeyT
) {
  return useFetch_old(
    `${url.GET_GLOBAL_REPORT_VIEWS_IMAGES}/${StudyUID}`,
    undefined,
    {
      enabled: !!StudyUID && enabled,
      refetch: false,
      refetchOnWindowFocus: false,
    },
    queryKey
  );
}

export function useGetGlobalReport(StudyUID: string, queryKey?: QueryKeyT) {
  return useFetch_old(
    `${url.GET_STATE_REPORT}/${StudyUID}`,
    undefined,
    {
      enabled: !!StudyUID,
      refetch: true,
      refetchOnWindowFocus: false,
    },
    queryKey
  );
}

export function useUpdateStudyReportWithFindings(
  onSuccess?: (response: AxiosResponse, data: Record<string, unknown>) => void,
  onError?: (responce: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const updateStudyReportWithFindings = async (data: Record<string, unknown>) => {
    const response = await apiClient.put(`${url.UPDATE_STATE_REPORT}`, data);
    return response.data?.data ?? response.data;
  };

  return useEdit<Record<string, unknown>>(
    updateStudyReportWithFindings,
    ['study_report_findings'],
    'Failed to update study report',
    'Study report updated successfully',
    onSuccess,
    onError,
    notification
  );
}

export function useUpdateComment(
  onSuccess?: (response: AxiosResponse, data: UpdateCommentRequest) => void,
  onError?: (responce: AxiosResponse, error: Error) => void
) {
  const updateComment = async (data: UpdateCommentRequest) => {
    const response = await apiClient.patch(
      `/study_reports/${data.study_instance_uid}/comment`,
      {
        comment: data.comment,
      }
    );
    return response.data;
  };

  return useEdit<UpdateCommentRequest>(
    updateComment,
    ['study_report_comment'],
    'Failed to update comment',
    'Comment updated successfully',
    onSuccess,
    onError
  );
}

export function useUpdateStateReports(
  onSuccess?: (response: AxiosResponse, data: Record<string, unknown>) => void,
  onError?: (responce: AxiosResponse, error: Error) => void
) {
  const updateStateReport = async (data: Record<string, unknown>) => {
    const response = await apiClient.put(`${url.UPDATE_STATE_REPORT}`, data);
    return response;
  };

  return useEdit<Record<string, unknown>>(
    updateStateReport,
    ['study_report_state'],
    'Failed to update report',
    'Report updated successfully',
    onSuccess,
    onError
  );
}
interface Filters {
  [key: string]: string | object;
}

interface PaginatedResponse {
  data: unknown;
}

export function usePaginatedReport(
  page: number,
  itemsPerPage: number,
  filters: Filters = {},
  config = {}
) {
  const getPaginatedExamsFn = async (
    p: number = page,
    f: Filters = filters
  ): Promise<PaginatedResponse> => {
    const processedFilters = Object.entries(f).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        acc[key] = typeof value === 'string' ? value : JSON.stringify(value);
        return acc;
      },
      {}
    );

    const filtersQuery = new URLSearchParams(processedFilters).toString();
    const url = `study_reports?page=${p}&items_per_page=${itemsPerPage}${
      filtersQuery ? `&${filtersQuery}` : ''
    }`;

    const response = await apiClient.get(url);
    return response.data;
  };

  return usePaginated(
    getPaginatedExamsFn,
    reportQueryKeys.pagination(page),
    page,
    config
  );
}

export function useDownloadPDFMutation(
  onSuccess?: (response: Blob, data: string) => void,
  onError?: (error: unknown, data: string) => void
) {
  const downloadPDF = async (studyId: string): Promise<Blob> => {
    const response = await apiClient.downloadFile(
      `report-sharing/${studyId}`
    );
    return response.data;
  };

  return useGenericMutation<string, Blob>(
    downloadPDF,
    ['ultrasound-pdf'],
    'Error downloading PDF',
    'PDF downloaded successfully',
    () => {},
    onSuccess as any,
    onError as any
  );
}

export function usePatientReports(patient_id: string) {
  return useFetch(
    async () => {
      const response = await apiClient.get(
        `/study_report/patient/${patient_id}`
      );
      return response;
    },
    ['reports'],
    {
      // refetchInterval: 60000 // Refetch every minute
    }
  );
}

export const useGetStudyReportGeneralInfo = (studyId: string) => {
  const fetchStudyInfo = async () => {
    const response = await apiClient.get(`/study_report/general/${studyId}`);
    return response;
  };
  return useSingleFetch(fetchStudyInfo, [`studyInfo/${studyId}`], studyId);
};

export function useDeleteStudyReportView(
  onSuccess?: () => void,
  onError?: (error: unknown) => void
) {
  const deleteFn = async (params: {
    studyInstanceUid: string;
    frameViewId: string;
  }) => {
    const response = await apiClient.delete(
      `${url.DELETE_STUDY_REPORT_VIEW}/${params.studyInstanceUid}/${params.frameViewId}`
    );
    return response.data;
  };

  return useGenericMutation<{
    studyInstanceUid: string;
    frameViewId: string;
  }>(
    deleteFn,
    ['study_report_view_delete'],
    'Failed to delete view',
    'View deleted successfully',
    () => {},
    onSuccess ? () => onSuccess() : undefined,
    onError ? (_resp: unknown, err: Error) => onError(err) : undefined
  );
}
